import React, { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useNavigate } from 'react-router-dom';
import { showSuccess, showError, showWarning } from '../utils/toastUtils';
import ConfirmModal from '../utils/ConfirmModal';
import '../stili/AggiornaNotizie.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function AggiornaNotizie() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [notizie, setNotizie] = useState([]);
  const [notiziaInModifica, setNotiziaInModifica] = useState(null);

  const [formData, setFormData] = useState({
    titolo: '',
    contenuto: '',
    autore: '',
    dataPubblicazione: '',
    stato: 'BOZZA',
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notiziaDaEliminare, setNotiziaDaEliminare] = useState(null);

  const fetchNotizie = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/notizie`);

      if (!res.ok) {
        let msg = 'Errore nel recupero delle notizie';
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await res.json();
      setNotizie(Array.isArray(data) ? data : []);
    } catch (err) {
      showError(err.message || 'Errore nel recupero delle notizie.');
      setNotizie([]);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchNotizie();
  }, [fetchNotizie]);

  const handleEditClick = (notizia) => {
    setNotiziaInModifica(notizia.id);
    setFormData({
      titolo: notizia.titolo || '',
      contenuto: notizia.contenuto || '',
      autore: notizia.autore || '',
      dataPubblicazione: notizia.dataPubblicazione || '',
      stato: notizia.stato || 'BOZZA',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'titolo' || name === 'autore') {
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      const res = await authFetch(`${API_URL}/api/admin/notizie/${notiziaInModifica}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        let msg = "Errore durante l'aggiornamento della notizia";
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      showSuccess('Notizia aggiornata con successo.');
      setNotiziaInModifica(null);
      setFormData({
        titolo: '',
        contenuto: '',
        autore: '',
        dataPubblicazione: '',
        stato: 'BOZZA',
      });
      fetchNotizie();
    } catch (err) {
      showError(err.message || "Errore durante l'aggiornamento.");
    }
  };

  const handleDeleteClick = (id) => {
    setNotiziaDaEliminare(id);
    setDeleteModalOpen(true);
  };

  const confermaEliminazione = async () => {
    if (!notiziaDaEliminare) return;

    try {
      const res = await authFetch(`${API_URL}/api/admin/notizie/${notiziaDaEliminare}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        let msg = "Errore durante l'eliminazione della notizia";
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      showSuccess('Notizia eliminata con successo.');

      if (notiziaInModifica === notiziaDaEliminare) {
        setNotiziaInModifica(null);
      }

      setDeleteModalOpen(false);
      setNotiziaDaEliminare(null);
      fetchNotizie();
    } catch (err) {
      showError(err.message || "Errore durante l'eliminazione.");
      setDeleteModalOpen(false);
      setNotiziaDaEliminare(null);
    }
  };

  const annullaEliminazione = () => {
    setDeleteModalOpen(false);
    setNotiziaDaEliminare(null);
    showWarning('Eliminazione annullata.');
  };

  const formatData = (data) => {
    if (!data) return '-';
    const parsed = new Date(data);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('it-IT');
  };

  const anteprimaContenuto = (contenuto) => {
    if (!contenuto) return '-';
    return contenuto.length > 180 ? `${contenuto.substring(0, 180)}...` : contenuto;
  };

  return (
    <div className="aggnotizie-container" translate="no">
      <h2 className="aggnotizie-title">Gestione Notizie</h2>

      {notizie.length === 0 ? (
        <div className="aggnotizie-empty-state">Nessuna notizia presente.</div>
      ) : (
        <div className="aggnotizie-cards-grid">
          {notizie.map((notizia) => (
            <article className="aggnotizie-card" key={notizia.id}>
              <div className="aggnotizie-card-header">
                <div className="aggnotizie-card-title-box">
                  <h3 className="aggnotizie-card-title">{notizia.titolo}</h3>
                  <p className="aggnotizie-card-date">
                    Pubblicazione: {formatData(notizia.dataPubblicazione)}
                  </p>
                </div>
              </div>

              <div className="aggnotizie-card-body">
                <div className="aggnotizie-info-row">
                  <span className="aggnotizie-info-label">Autore</span>
                  <span className="aggnotizie-info-value">{notizia.autore || '-'}</span>
                </div>

                <div className="aggnotizie-info-row">
                  <span className="aggnotizie-info-label">Stato</span>
                  <span className="aggnotizie-info-value">{notizia.stato || '-'}</span>
                </div>

                <div className="aggnotizie-content-box">
                  <span className="aggnotizie-info-label">Contenuto</span>
                  <p className="aggnotizie-content-preview">
                    {anteprimaContenuto(notizia.contenuto)}
                  </p>
                </div>
              </div>

              <div className="aggnotizie-actions">
                <button
                  type="button"
                  className="aggnotizie-btn-primary"
                  onClick={() => handleEditClick(notizia)}
                >
                  Modifica
                </button>

                <button
                  type="button"
                  className="aggnotizie-btn-danger"
                  onClick={() => handleDeleteClick(notizia.id)}
                >
                  Elimina
                </button>
              </div>

              {notiziaInModifica === notizia.id && (
                <div className="aggnotizie-edit-card">
                  <h4 className="aggnotizie-section-title">Modifica notizia</h4>

                  <form onSubmit={handleUpdate}>
                    <div className="aggnotizie-grid-2">
                      <div className="aggnotizie-form-group">
                        <label className="aggnotizie-label">Titolo</label>
                        <input
                          className="aggnotizie-input"
                          type="text"
                          name="titolo"
                          value={formData.titolo}
                          onChange={handleChange}
                          placeholder="Titolo"
                          required
                        />
                      </div>

                      <div className="aggnotizie-form-group">
                        <label className="aggnotizie-label">Autore</label>
                        <input
                          className="aggnotizie-input"
                          type="text"
                          name="autore"
                          value={formData.autore}
                          onChange={handleChange}
                          placeholder="Autore"
                          required
                        />
                      </div>

                      <div className="aggnotizie-form-group">
                        <label className="aggnotizie-label">Data pubblicazione</label>
                        <input
                          className="aggnotizie-input"
                          type="date"
                          name="dataPubblicazione"
                          value={formData.dataPubblicazione}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="aggnotizie-form-group">
                        <label className="aggnotizie-label">Stato</label>
                        <select
                          className="aggnotizie-select"
                          name="stato"
                          value={formData.stato}
                          onChange={handleChange}
                          required
                        >
                          <option value="BOZZA">BOZZA</option>
                          <option value="PUBBLICATA">PUBBLICATA</option>
                          <option value="NASCOSTA">NASCOSTA</option>
                        </select>
                      </div>

                      <div className="aggnotizie-form-group aggnotizie-full-width">
                        <label className="aggnotizie-label">Contenuto</label>
                        <textarea
                          className="aggnotizie-textarea"
                          name="contenuto"
                          value={formData.contenuto}
                          onChange={handleChange}
                          placeholder="Contenuto"
                          required
                        />
                      </div>
                    </div>

                    <div className="aggnotizie-actions">
                      <button type="submit" className="aggnotizie-btn-primary">
                        Salva
                      </button>

                      <button
                        type="button"
                        className="aggnotizie-btn-secondary"
                        onClick={() => setNotiziaInModifica(null)}
                      >
                        Annulla
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="aggnotizie-button-row">
        <button
          className="aggnotizie-btn-secondary"
          type="button"
          onClick={() => navigate('/gestionale')}
        >
          Torna al Gestionale
        </button>
      </div>

      <ConfirmModal
        open={deleteModalOpen}
        title="Elimina notizia"
        message="Sei sicuro di voler eliminare questa notizia? L'operazione non può essere annullata."
        confirmText="Elimina"
        cancelText="Annulla"
        danger={true}
        onConfirm={confermaEliminazione}
        onCancel={annullaEliminazione}
      />
    </div>
  );
}